import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { createTransaction, getTransaction } from '../services/transactionsService';
import { sendPurchaseNotification, finalizePaymentReservation } from '../services/paymentsService';
import { updateCartItemAction, loadCartItemsByStatus } from '../services/cartItemsService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../sdk_initialization';
import { sendEmail } from '../services/emailService';
import { writeReservationLog } from '../services/reservationLogsService';
import { sfVendorRegistration } from '../services/defaultsService';

import './ProfilePage.css';

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const formatUsdFromCents = (cents) => usdFormatter.format(Number(cents || 0) / 100);
const PAYMENT_SUCCESS_CONTEXT_KEY = 'paymentSuccessContext';

const readStoredPaymentContext = (paymentIntentId) => {
  try {
    const specific = paymentIntentId
      ? sessionStorage.getItem(`${PAYMENT_SUCCESS_CONTEXT_KEY}:${paymentIntentId}`)
      : null;
    const fallback = sessionStorage.getItem(`${PAYMENT_SUCCESS_CONTEXT_KEY}:last`);
    const raw = specific || fallback;
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (!paymentIntentId || !parsed?.paymentIntentId || parsed.paymentIntentId === paymentIntentId) {
      return parsed;
    }
  } catch (storageErr) {
    console.warn('[PaymentSuccess] Unable to read stored payment context:', storageErr?.message || storageErr);
  }

  return null;
};

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const hasFinalized = useRef(false); // Prevent double execution
  
  const searchParams = new URLSearchParams(location.search || '');
  const stateData = location.state || {};
  const queryPaymentIntentId = searchParams.get('payment_intent') || searchParams.get('paymentIntentId') || '';
  const storedContext = readStoredPaymentContext(stateData?.paymentIntentId || queryPaymentIntentId);
  const paymentContext = {
    ...(storedContext || {}),
    ...stateData,
    metadata: {
      ...((storedContext && storedContext.metadata) || {}),
      ...(stateData.metadata || {}),
    },
  };

  const {
    paymentIntentId: statePaymentIntentId = '',
    amount = 0,
    eventName = '',
    booths = [],
    metadata = {},
    eventId: topLevelEventId = '',
    layoutId: topLevelLayoutId = '',
  } = paymentContext;

  const paymentIntentId = statePaymentIntentId || queryPaymentIntentId;

  const resolvedEventName = (eventName ?? metadata?.eventName) || '';

  const handlePrint = () => {
    window.print();
  };

  useEffect(() => {
    if (!paymentIntentId) {
      navigate('/');
      return;
    }

    // Wait for auth context; otherwise we can mark the flow as finalized too early
    // and permanently skip reservation finalization on this page load.
    if (!currentUser?.uid) {
      return;
    }

    // Prevent double execution (React 18 strict mode runs effects twice in dev)
    if (hasFinalized.current) {
      return;
    }
    hasFinalized.current = true;

    // Save transaction and mark booths as Reserved
    const finalizePayment = async () => {
      setSaving(true);
      
      try {
        // Resolve eventId and layoutId: prefer top-level state, fall back to metadata
        const eventId = topLevelEventId || metadata?.eventId || '';
        const layoutId = topLevelLayoutId || metadata?.layoutId || '';

        // Clear the checkout started flag since payment is complete
        try {
          const checkoutKey = `checkoutStarted:${currentUser.uid}:${eventId}:${layoutId}`;
          sessionStorage.removeItem(checkoutKey);
        } catch (e) {
          // Could not clear checkout flag
        }

        // 1. Save transaction to permanent collection
        // First check if there's an existing transaction with failure attempts
        let existingFailureAttempts = [];
        let existingAttemptCount = 0;
        try {
          const existingTx = await getTransaction(paymentIntentId);
          if (existingTx) {
            existingFailureAttempts = existingTx.failureAttempts || [];
            existingAttemptCount = existingTx.attemptCount || 0;
          }
        } catch (err) {
          // Could not fetch existing transaction
        }
        if (!eventId) {
          try {
            // One-time debug: log when eventId could not be resolved for finalizing transaction
            // eslint-disable-next-line no-console
            console.warn('[PaymentSuccess] createTransaction: no eventId resolved for paymentIntentId', paymentIntentId, 'metadata:', metadata);
          } catch (e) {}
        }

        await createTransaction({
          paymentIntentId,
          profileId: currentUser.uid,
          eventId: eventId || '',
          layoutId: layoutId || null,
          amount,
          currency: 'usd',
          status: 'succeeded',
          metadata,
          booths: Array.isArray(booths) ? booths.map(b => ({
            id: typeof b === 'object' ? (b.id || '') : '',
            number: typeof b === 'object' ? (b.number || b) : b,
            level: typeof b === 'object' ? (b.level || '') : '',
            corner: typeof b === 'object' ? (b.corner || '') : '',
            row: typeof b === 'object' ? (b.row || 0) : 0,
            col: typeof b === 'object' ? (b.col || 0) : 0,
            price: typeof b === 'object' ? (b.price || 0) : 0,
          })) : [],
          // Preserve failure attempts from previous failed attempts
          failureAttempts: existingFailureAttempts,
          attemptCount: existingAttemptCount,
        });

        // 2. Reservation finalization with webhook-first and callable fallback.
        // We poll webhook status first; if still pending/failed we trigger backend
        // callable finalization (provider-verified and idempotent).
        const txForFallback = await getTransaction(paymentIntentId).catch(() => null);
        const resolvedEventId = eventId || txForFallback?.eventId || metadata?.eventId || '';
        const resolvedLayoutId = layoutId || txForFallback?.layoutId || metadata?.layoutId || '';
        const resolvedVendorId =
          metadata?.vendorId ||
          txForFallback?.metadata?.vendorId ||
          txForFallback?.vendorId ||
          '';
        const resolvedBooths = (Array.isArray(booths) && booths.length > 0)
          ? booths
          : (Array.isArray(txForFallback?.booths) ? txForFallback.booths : []);
        const isBoothPurchase = String(
          metadata?.purchaseType ||
          txForFallback?.metadata?.purchaseType ||
          ((Array.isArray(resolvedBooths) && resolvedBooths.length > 0) ? 'booths' : ''),
        ).toLowerCase() === 'booths';

        if (isBoothPurchase) {
          let latestTx = null;
          let finalizationStatus = '';

          try {
            latestTx = await getTransaction(paymentIntentId);
            finalizationStatus = String(latestTx?.reservationFinalizationStatus || '').toLowerCase();
          } catch (txReadErr) {
            console.warn('[PaymentSuccess] Unable to read transaction finalization status:', txReadErr?.message || txReadErr);
          }

          // Trigger fallback immediately when webhook has not already completed.
          if (!finalizationStatus || finalizationStatus === 'pending' || finalizationStatus === 'failed' || finalizationStatus === 'partial_failure') {
            try {
              const finalizeResult = await finalizePaymentReservation({
                paymentProvider: 'stripe',
                paymentReferenceId: paymentIntentId,
                eventId: resolvedEventId,
                layoutId: resolvedLayoutId,
                vendorId: resolvedVendorId,
                createdByProfileId: currentUser.uid,
                sessionId: metadata?.sessionId || null,
                booths: resolvedBooths,
              });

              // Refresh status after fallback finalization attempt for accurate user feedback.
              latestTx = await getTransaction(paymentIntentId).catch(() => latestTx);
              finalizationStatus = String(latestTx?.reservationFinalizationStatus || '').toLowerCase();

              console.info('[PaymentSuccess] finalizePaymentReservation fallback result:', {
                paymentIntentId,
                success: finalizeResult?.success,
                reservationFinalizationStatus: finalizeResult?.reservationFinalizationStatus,
                failedCount: Array.isArray(finalizeResult?.failed) ? finalizeResult.failed.length : 0,
              });
            } catch (fallbackErr) {
              console.error('[PaymentSuccess] finalizePaymentReservation fallback failed:', fallbackErr?.message || fallbackErr);
            }
          }

          if (!finalizationStatus || finalizationStatus === 'pending') {
            const pollStart = Date.now();
            const pollTimeoutMs = 10000;
            const pollIntervalMs = 1000;

            while (Date.now() - pollStart < pollTimeoutMs) {
              try {
                latestTx = await getTransaction(paymentIntentId);
                finalizationStatus = String(latestTx?.reservationFinalizationStatus || '').toLowerCase();
                if (finalizationStatus && finalizationStatus !== 'pending') {
                  break;
                }
              } catch (txReadErr) {
                console.warn('[PaymentSuccess] Unable to refresh transaction finalization status:', txReadErr?.message || txReadErr);
              }

              await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
            }
          }

          const failedSummary = latestTx?.reservationFinalizeSummary?.failedBooths;
          const failed = Array.isArray(failedSummary)
            ? failedSummary.map((f) => ({ boothId: f?.boothId || null, reason: f?.reason || 'unknown' }))
            : [];

          if (!finalizationStatus || finalizationStatus === 'pending') {
            console.warn('[PaymentSuccess] Reservation finalization still pending; webhook completion may still be in progress', {
              paymentIntentId,
              eventId: resolvedEventId,
              layoutId: resolvedLayoutId,
              vendorId: resolvedVendorId,
              boothsLength: Array.isArray(resolvedBooths) ? resolvedBooths.length : 0,
            });
          }

          // Separate conflicts from other failures for better user messaging
          const conflicts = failed.filter(f => 
            String(f.reason || '').toLowerCase().includes('conflict') || 
            String(f.reason || '').toLowerCase().includes('already reserved')
          );
          const otherFailures = failed.filter(f => !conflicts.includes(f));

          if (failed.length > 0) {
            try {
              // Build user-friendly message distinguishing conflicts from other failures
              let userMessage = 'Payment succeeded, but some booths could not be reserved:';
              
              if (conflicts.length > 0) {
                const conflictList = conflicts.map(f => `• ${f.boothId}`).join('\n');
                userMessage += `\n\n[ALREADY RESERVED BY ANOTHER VENDOR]\n${conflictList}\n\nThese booths were claimed by another vendor while your payment was processing. This is a timing issue, not a payment problem. Please try reserving different booths.`;
              }

              if (otherFailures.length > 0) {
                const failureList = otherFailures.map(f => `• ${f.boothId}: ${f.reason}`).join('\n');
                userMessage += `\n\n[OTHER ISSUES]\n${failureList}\n\nOur team has been notified and will assist you.`;
              }

              window.alert(userMessage);

              // Send email with detailed information
              const toEmail = metadata?.payeeEmail || metadata?.vendorEmail || currentUser.email || '';
              const subject = `Issue reserving booths after payment (${paymentIntentId})`;
              
              let bodyDetails = '';
              if (conflicts.length > 0) {
                bodyDetails += `<h3>Booths Already Reserved</h3><p>The following booths were claimed by another vendor while your payment was processing:</p><ul>` +
                  conflicts.map(f => `<li>${f.boothId}</li>`).join('') +
                  `</ul><p>This is due to timing and not a problem with your payment. Please log back in and reserve different booths.</p>`;
              }
              if (otherFailures.length > 0) {
                bodyDetails += `<h3>Other Issues</h3><ul>` +
                  otherFailures.map(f => `<li>${f.boothId}: ${f.reason}</li>`).join('') +
                  `</ul>`;
              }

              const body = `Dear ${metadata?.vendorName || 'Vendor'},<br/><br/>` +
                `Your payment (ID: <strong>${paymentIntentId}</strong>) was successful, but we encountered the following issues while reserving your booths:<br/><br/>` +
                bodyDetails +
                `<p>If you believe this is an error or need assistance, please reply to this message or contact bazaar@conv.icna.org.</p><p>Thank you,<br/>ICNACON Bazaar Team</p>`;
              
              if (toEmail) await sendEmail({ to: toEmail, subject, type: 'HTML', body });
              
              // Write failure logs for admin triage (best-effort)
              try {
                for (const f of failed) {
                  try {
                    await writeReservationLog({
                      type: 'finalize-failed',
                      reservationId: `${resolvedEventId}_${resolvedLayoutId}_${f.boothId}`,
                      eventId: resolvedEventId,
                      layoutId: resolvedLayoutId,
                      boothId: f.boothId,
                      previousStatus: null,
                      previousProfileId: null,
                      previousSessionId: null,
                      previousCreatedAt: null,
                      previousExpiresAt: null,
                      triggeredBy: 'PaymentSuccess',
                      triggeredByProfileId: currentUser?.uid || null,
                      metadata: { 
                        paymentIntentId, 
                        reason: f.reason,
                        isConflict: conflicts.includes(f),
                      },
                    });
                  } catch (e) {}
                }
              } catch (logErr) {
                console.error('[PaymentSuccess] Failed to write reservation failure logs', logErr);
              }
            } catch (notifyErr) {
              console.error('[PaymentSuccess] Failed to notify user about reservation failures', notifyErr);
            }
          }
        } else {
          // Only warn when this was supposed to be a booths purchase but required data is missing.
          if (isBoothPurchase) {
            console.warn('[PaymentSuccess] Cannot mark booths as Reserved - missing data:', {
              eventId: resolvedEventId,
              layoutId: resolvedLayoutId,
              vendorId: resolvedVendorId,
              boothsLength: resolvedBooths?.length
            });
          } else {
            // Normal for non-booth purchases (attendees/manual); nothing to reserve.
            console.debug('[PaymentSuccess] No booths to reserve for this transaction', {
              purchaseType: metadata?.purchaseType,
              eventId: resolvedEventId,
              layoutId: resolvedLayoutId,
              vendorId: resolvedVendorId,
              boothsLength: resolvedBooths?.length
            });
          }
        }

        // 3. Clear cart items for all purchased booths
        if (eventId && layoutId) {

          try {
            // Get the session ID from sessionStorage (same key pattern as BoothSelection)
            const sessionKey = `cartSession:${currentUser.uid}:${eventId}:${layoutId}`;
            const sessionId = sessionStorage.getItem(sessionKey);
            
            if (!sessionId) {
              // No sessionId found, cannot clear cart items
              
            } else {

              // Load all checkedout cart items for this user/event/layout/session
              const checkedoutItems = await loadCartItemsByStatus({
                profileId: currentUser.uid,
                eventId,
                layoutId,
                sessionId,
                status: 'checkedout'
              });
              
              
              
              // Mark all cart items as 'paid' to complete the purchase
              for (const cartItem of checkedoutItems) {
                try {
                  await updateCartItemAction(cartItem.cartItemId || cartItem.id, 'paid', {
                    triggeredByProfileIdOverride: currentUser.uid,
                    triggeredByOverride: 'payment-success'
                  });
                } catch (err) {
                  console.error('[PaymentSuccess] Failed to mark cart item as paid:', cartItem.cartItemId || cartItem.id, err);
                  // Don't fail the whole process if one cart item fails
                }
              }
            }
          } catch (cartErr) {
            console.error('[PaymentSuccess] ❌ Failed to clear cart items:', cartErr);
            // Don't fail the whole process if cart clearing fails
          } 
        }

        setSaveError(''); // Clear any previous errors

        // 5. Send confirmation email (best-effort, do not block finalization)
        try {
          let eventAdminContactEmail = '';
          if (eventId) {
            try {
              const eventRef = doc(db, 'events', eventId);
              const eventSnap = await getDoc(eventRef);
              if (eventSnap.exists()) {
                eventAdminContactEmail = String(eventSnap.data()?.eventAdminContactEmail || '').trim();
              }
            } catch (eventReadErr) {
              console.warn('[PaymentSuccess] Could not load eventAdminContactEmail for notification:', eventReadErr?.message || eventReadErr);
            }
          }

          const toEmail = metadata?.payeeEmail || metadata?.vendorEmail || currentUser.email || '';
          if (toEmail) {
            const data = {
              toEmail: toEmail,
              paidAmount: amount === 0 ? 0 : (amount / 100).toFixed(2),
              purchaseType: metadata?.purchaseType || 'manual',
              paymentId: paymentIntentId || '',
              eventName: resolvedEventName || 'ICNA National Convention',
              vendorName: metadata?.vendorName || 'Vendor',
              boothList: booths.map(b => (typeof b === 'object' && b !== null ? b.number : b)).join(', '),
              attendeeCount: metadata?.attendeeCount || 0,
              eventAdminContactEmail,
            }
            sendPurchaseNotification(data);
          } else {
            console.warn('[PaymentSuccess] No recipient email found; skipping confirmation email');
          }
        } catch (emailBuildErr) {
          console.error('[PaymentSuccess] Error while preparing confirmation email:', emailBuildErr);
        }

        // 6. Fire-and-forget: notify external SF vendor registration (don't block finalization)
        try {
          const vendorIdForSf = metadata?.vendorId || '';
          const eventIdForSf = eventId || metadata?.eventId || '';
          if (vendorIdForSf && eventIdForSf) {
            const appSettings = { enableLogs: false, enableEncryption: true };
            // intentionally do not await; handle errors in the promise rejection handler
            sfVendorRegistration(appSettings, eventIdForSf, vendorIdForSf).catch(err => {
              // Non-blocking: log but do not surface error to user
              console.warn('[PaymentSuccess] sfVendorRegistration failed (non-blocking):', err && err.message ? err.message : err);
            });
          }
        } catch (err) {
          console.warn('[PaymentSuccess] Error initiating sfVendorRegistration:', err && err.message ? err.message : err);
        }

      } catch (error) {
        console.error('[PaymentSuccess] Error finalizing payment:', error);
        setSaveError(error.message || 'Failed to finalize payment');
      } finally {
        setSaving(false);
      }
    };

    finalizePayment();
  }, [paymentIntentId, currentUser?.uid, amount, metadata, booths, navigate]);

  return (
    <div className="profile-container" style={{ color: '#333' }}>
      <div className="profile-header-row">
        <div style={{ flex: 1 }} />
        <button type="button" className="cancel-btn" onClick={() => navigate('/')}>
          Close
        </button>
      </div>

      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
        {/* Success Icon */}
        <div style={{ 
          fontSize: '64px', 
          marginBottom: 20,
          animation: 'scaleIn 0.3s ease-in-out'
        }}>
          ✅
        </div>

        <h2 className="profile-title" style={{ color: '#155724', marginBottom: 12 }}>
          Payment Successful!
        </h2>

        <p style={{ fontSize: '1.1em', color: '#666', marginBottom: 30 }}>
          Thank you for your payment. Your transaction has been completed.
        </p>

        {/* Print Notice */}
        <div style={{ 
          padding: 16,
          marginBottom: 30,
          backgroundColor: '#e7f3ff',
          border: '1px solid #b3d9ff',
          borderRadius: 4,
          color: '#004085',
          maxWidth: 600,
          margin: '0 auto 30px'
        }}>
          <p style={{ margin: 0, fontWeight: 500 }}>
            💡 You can print or save this page for your records as proof of payment.
          </p>
        </div>

        {saving && (
          <div style={{ 
            padding: 12,
            marginBottom: 20,
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: 4,
            color: '#856404'
          }}>
            Saving transaction record...
          </div>
        )}

        {saveError && (
          <div style={{ 
            padding: 12,
            marginBottom: 20,
            backgroundColor: '#f8d7da',
            border: '1px solid #f5c6cb',
            borderRadius: 4,
            color: '#721c24'
          }}>
            ⚠️ {saveError}
          </div>
        )}

        {/* Payment Details */}
        <div style={{ 
          maxWidth: 500, 
          margin: '0 auto',
          padding: 20,
          backgroundColor: '#f8f9fa',
          borderRadius: 8,
          textAlign: 'left'
        }}>
          <h3 style={{ marginTop: 0, marginBottom: 16, color: '#333' }}>Payment Details</h3>
          
          <div style={{ marginBottom: 12 }}>
            <strong style={{ color: '#333' }}>Payment ID:</strong>
            <div style={{ 
              fontFamily: 'monospace', 
              fontSize: '0.9em',
              color: '#666',
              marginTop: 4,
              wordBreak: 'break-all'
            }}>
              {paymentIntentId}
            </div>
          </div>

          {resolvedEventName && (
            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: '#333' }}>Event:</strong>
              <div style={{ color: '#666', marginTop: 4 }}>{resolvedEventName}</div>
            </div>
          )}

          {metadata?.vendorName && (
            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: '#333' }}>Vendor:</strong>
              <div style={{ color: '#666', marginTop: 4 }}>{metadata.vendorName}</div>
            </div>
          )}

          {booths && booths.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <strong style={{ color: '#333' }}>Booths Reserved:</strong>
              <div style={{ color: '#666', marginTop: 4 }}>
                {booths.map(b => typeof b === 'object' && b !== null ? b.number : b).join(', ')}
              </div>
            </div>
          )}

          <div style={{ 
            marginTop: 20,
            paddingTop: 20,
            borderTop: '2px solid #dee2e6'
          }}>
            <strong style={{ color: '#333' }}>Amount Paid:</strong>
            <div style={{ 
              fontSize: '1.5em',
              fontWeight: 'bold',
              color: '#155724',
              marginTop: 8
            }}>
              {formatUsdFromCents(amount)}
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div style={{ marginTop: 40, display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <button
            onClick={handlePrint}
            className="back-btn"
            style={{ minWidth: 150, backgroundColor: '#28a745', color: '#fff' }}
          >
            🖨️ Print Receipt
          </button>

          <button
            onClick={() => navigate('/')}
            className="back-btn"
            style={{ minWidth: 150, backgroundColor: '#1976d2', color: '#fff' }}
          >
            Go to Home
          </button>
          
          <button
            onClick={() => navigate('/booth-selection', { state: { backTo: '/payment-success' } })}
            className="back-btn"
            style={{ minWidth: 150 }}
          >
            View My Booths
          </button>
        </div>

        {/* Confirmation Message */}
        <div style={{ 
          marginTop: 40,
          padding: 16,
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: 4,
          color: '#155724'
        }}>
          <p style={{ margin: 0 }}>
            📧 A confirmation email has been sent to your registered email address.
            Please check your inbox for payment receipt and booth reservation details. 
            However, if you don't find an email, you can always look at the info by clicking the My Payments button  of the home page.
          </p>
        </div>
      </div>

      <style>
        {`
          @keyframes scaleIn {
            from {
              transform: scale(0);
              opacity: 0;
            }
            to {
              transform: scale(1);
              opacity: 1;
            }
          }

          @media print {
            .profile-header-row,
            button {
              display: none !important;
            }
            
            .profile-container {
              background: white !important;
            }
            
            body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
          }
        `}
      </style>
    </div>
  );
};

export default PaymentSuccess;
